import { useCallback, useState } from "react"

import { CloseIcon } from "@chakra-ui/icons"
import {
  Box,
  Button,
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Text,
  useDisclosure,
} from "@chakra-ui/react"
import { AxiosError } from "axios"

import { useAppSelector } from "app/hooks"
import Counter from "components/floor-plan/counter"
import useToastHook from "hooks/useToastHook/useToastHook"
import { apiClient } from "services/client"
import { selectCurrentPmc } from "store/auth/authSlice"

type AddWeightPopupPropType = {
  checkedProperties: string[]
  noOfSelectedItems: number
  mutate: () => void
}

const AddWeightPopup = (props: AddWeightPopupPropType) => {
  const { noOfSelectedItems, checkedProperties, mutate } = props
  const { onOpen, onClose, isOpen } = useDisclosure()
  const displayToast = useToastHook()
  const currentPmc = useAppSelector(selectCurrentPmc)

  const [updatedWeightValue, setUpdatedWeightValue] = useState<number>(1)
  const [isDisabledApply, setIsDisabledApply] = useState(false)

  const handleUpdatedWeightApply = useCallback(() => {
    setIsDisabledApply(true)
    if (currentPmc?.pmc_id) {
      apiClient
        .updateWeight({
          params: {
            pmcId: currentPmc?.pmc_id,
            weight: updatedWeightValue,
            pmsUnitIds: checkedProperties,
          },
        })
        .then((response) => {
          onClose()
          setIsDisabledApply(false)
          setUpdatedWeightValue(1)
          mutate()
          if (response.data.apiStatus === "SUCCESS") {
            displayToast({
              message: response.data.message,
            })
          } else {
            displayToast({
              message: response.data.message,
            })
          }
          return true
        })
        .catch((e: AxiosError) => {
          displayToast({
            message: e.message,
            variant: "error",
          })
          onClose()
          setIsDisabledApply(false)
          setUpdatedWeightValue(1)
        })
    }
  }, [
    currentPmc?.pmc_id,
    updatedWeightValue,
    checkedProperties,
    displayToast,
    onClose,
    mutate,
  ])

  return (
    <Popover
      isOpen={isOpen}
      onOpen={onOpen}
      onClose={() => {
        onClose()
        setUpdatedWeightValue(1)
      }}
      placement='bottom-end'
    >
      <PopoverTrigger>
        <Button
          variant='secondary'
          size='sm'
          isDisabled={noOfSelectedItems === 0}
        >
          Add Weights
        </Button>
      </PopoverTrigger>
      <PopoverContent>
        <PopoverBody pr={4}>
          <Box
            display='flex'
            alignItems='center'
            justifyContent='space-between'
          >
            <Text variant='black' size='sm' fontWeight={600}>
              Weight Value
            </Text>
            <CloseIcon
              width='10px'
              height='10px'
              cursor='pointer'
              onClick={() => {
                onClose()
                setUpdatedWeightValue(1)
              }}
            />
          </Box>
          <Text variant='black' size='sm' mt={2.5}>
            {`Add a weight value to the selected ${
              noOfSelectedItems > 1 ? "properties" : "property"
            }`}
          </Text>
          <Box
            display='flex'
            alignItems='center'
            justifyContent='space-between'
            mt={3}
            mb={2.5}
          >
            <Counter
              name=''
              value={updatedWeightValue}
              decrementCounter={() =>
                setUpdatedWeightValue(updatedWeightValue - 1)
              }
              incrementCounter={() =>
                setUpdatedWeightValue(updatedWeightValue + 1)
              }
              borderColor='#006f80'
              iconWidth='29px'
            />
            <Button
              variant='tertiaryGradient'
              size='sm'
              px={5}
              height='30px'
              onClick={handleUpdatedWeightApply}
              isDisabled={isDisabledApply}
            >
              Apply
            </Button>
          </Box>
        </PopoverBody>
      </PopoverContent>
    </Popover>
  )
}

export default AddWeightPopup
